import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Grid,
  Link,
} from '@mui/material';
import { CloudUpload, CheckCircle } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { authService } from '../services/authService';

const PesticideLicenseUploadPage: React.FC = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [licenseDocument, setLicenseDocument] = useState<string>('');
  const [licenseUploading, setLicenseUploading] = useState(false);
  const [licenseDetails, setLicenseDetails] = useState({
    licenseNumber: '',
    expiryDate: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Redirect if not a provider
    if (user && user.userType !== 'provider') {
      navigate('/');
    }
    
    // If license already on file, show success message
    if (user && (user as any).pesticideLicenseOnFile) {
      setSuccess(true);
    }
  }, [user, navigate]);

  const handleLicenseUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type (PDF or image)
    const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      setError('License document must be a PDF or image file (JPG, PNG)');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('License document must be smaller than 10MB');
      return;
    }

    try {
      setLicenseUploading(true);
      setError('');

      // Convert to base64
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64String = e.target?.result as string;
        setLicenseDocument(base64String);
        setLicenseUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (err: any) {
      setError(err.message || 'Failed to upload license document');
      setLicenseUploading(false);
    }
  };

  const handleLicenseDetailsChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const target = e.target as HTMLInputElement;
    setLicenseDetails({
      ...licenseDetails,
      [target.name]: target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!licenseDocument) {
      setError('License document is required');
      setLoading(false);
      return;
    }

    if (!licenseDetails.licenseNumber) {
      setError('License number is required');
      setLoading(false);
      return;
    }

    try {
      const response = await apiService.put('/providers/pesticide-license', {
        pesticideLicenseDocument: licenseDocument,
        pesticideLicenseNumber: licenseDetails.licenseNumber,
        pesticideLicenseExpiryDate: licenseDetails.expiryDate || undefined,
        pesticideLicenseOnFile: true,
        pesticideLicenseSubmittedDate: new Date().toISOString(),
      });

      if (response.success) {
        setSuccess(true);
        // Refresh user data to update license status
        try {
          const updatedUser = await authService.getCurrentUser();
          await updateUser(updatedUser);
        } catch (err) {
          console.error('Failed to refresh user data:', err);
          // Continue anyway - user will see updated data on next page load
        }
        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          navigate('/provider/dashboard');
        }, 2000);
      } else {
        setError(response.error || 'Failed to submit pesticide license');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to submit pesticide license');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Pesticide License Submitted Successfully!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Your IDOA Commercial Pesticide Applicator License has been received. You can now bid on weed control service requests.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/provider/dashboard')}>
            Return to Dashboard
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Pesticide License Upload
        </Typography>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="bold">
            IDOA Commercial Pesticide Applicator License Required
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Providers offering Weed Control/Fertilizing services must upload a copy of their current IDOA-issued Commercial Pesticide Applicator License. 
            You cannot bid on weed control service requests until your license is on file.
          </Typography>
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <input
                  accept="application/pdf,image/jpeg,image/jpg,image/png"
                  style={{ display: 'none' }}
                  id="license-upload"
                  type="file"
                  onChange={handleLicenseUpload}
                  disabled={licenseUploading || loading}
                />
                <label htmlFor="license-upload">
                  <Button
                    variant="contained"
                    component="span"
                    color={licenseDocument ? 'success' : 'primary'}
                    startIcon={licenseUploading ? <CircularProgress size={20} /> : <CloudUpload />}
                    disabled={licenseUploading}
                    fullWidth
                    sx={{ py: 1.5 }}
                  >
                    {licenseUploading ? 'Uploading...' : licenseDocument ? 'License Document Uploaded ✓' : 'Upload License Document (Required)'}
                  </Button>
                </label>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                  PDF or image (JPG, PNG), max 10MB
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="licenseNumber"
                label="License Number"
                name="licenseNumber"
                placeholder="Enter your IDOA license number"
                value={licenseDetails.licenseNumber}
                onChange={handleLicenseDetailsChange}
                disabled={loading}
                helperText="Enter your IDOA Commercial Pesticide Applicator License number"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="expiryDate"
                label="License Expiry Date (Optional)"
                name="expiryDate"
                type="date"
                value={licenseDetails.expiryDate}
                onChange={handleLicenseDetailsChange}
                disabled={loading}
                InputLabelProps={{
                  shrink: true,
                }}
                helperText="If your license has an expiry date, enter it here"
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/provider/dashboard')}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading || !licenseDocument || !licenseDetails.licenseNumber}
                >
                  {loading ? <CircularProgress size={24} /> : 'Submit License'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
};

export default PesticideLicenseUploadPage;

